import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { IconButton, Typography } from "@material-ui/core";
import SearchInput from "@/components/SearchInput";
import { IconGroups, IconSwitch } from "@/components/Icon";
import Collapser from "@/components/Collapser";
import { useTranslation } from "@/langs/useTranslation";
import LoadingIndicator from "@/components/commons/LoadingIndicator";
import { flattenTreeData } from "@/helpers/utils";
import StoreApi from "@/apis/storeAPI"
import {
  createGroupApi,
  deleteGroupApi,
  moveGroupAPI,
  updateGroupApi
} from "@/apis/apiCollection";
import CategoryTree from "./CategoryTree";
import styles from "./index.module.less";
import CompanySelector from "../CompanyTree/Selector";
import localforage from 'localforage';
import { useGroupDeviceDispatch, useGroupDeviceState } from "../Provider"

const CategoryContent = ({ collapse, children }) => {
  const { t } = useTranslation();
  if (collapse) {
    return (
      <Collapser
        style={{ marginTop: 16 }}
        title={
          <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
            <IconGroups size={16} color="primary" style={{ marginRight: 5 }} />
            {t({ defaultMessage: "全部门店列表" })}
          </Typography>
        }
      >
        {children}
      </Collapser>
    );
  }
  return children;
};


const Category = React.forwardRef(
  (
    {
      showSwitch = true,
      showAll = true,
      collapse = true,
      checkStrictly,
      selectable = true,
      editable = true,
      sortable = true,
      importable = true,
      withPermission,
      readOnly,
      userMode,
      description,
      defaultSelectAll,
      disabledCheckAllNode,
      checkable: checkableFromProps,
      selectedKeys: selectedKeysFromProps,
      checkedKeys: checkedKeysFromProps,
      onCheck,
      onSelect,
      onSwitch,
      onChangeRename,
      onInputRename,
      onNodeImport,
      onLoaded,
      onLoadFailed
    },
    ref
  ) => {

    // console.log("-------readOnly----ggg----",readOnly)
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [treeData, setTreeData] = useState([]);
    const [checkable, setCheckable] = useState(false);

    const [searchValue, setSearchValue] = useState("");
    const [expandedKeys, setExpandedKeys] = useState();
    const [selectedKeys, setSelectedKeys] = useState();
    const [checkedKeys, setCheckedKeys] = useState();
    const selectedKeysFromPropsRef = useRef(null);

    const {currentCompanyId,currentCompanyName} = useGroupDeviceState()

    // const globalDispatch = useGlobalDispatch();
    const handleCompanySelect = (companyId, info) => {
      // setCurrentCompanyId(companyId[0])
      // globalDispatch.setStoreManagementCompany(companyId[0], info.node.name);
    };

    // const [currentCompanyId, setCurrentCompanyId] = useState(null);


    selectedKeysFromPropsRef.current = selectedKeysFromProps;

    const treeTopNodeNameMap = {
      all: userMode
        ? t({ defaultMessage: "所有账号" })
        : t({ defaultMessage: "所有门店" }),
      default: t({ defaultMessage: "仓储" })
    };

    const formatTree = tree => {
      return tree.map((item, i) => {
        const id = item.id;
        const name = item.name;
        const isRoot = name === "all" || name === "default";
        const nodeName = isRoot ? treeTopNodeNameMap[name] || name : name;
        return {
          key: id,
          id,
          title: nodeName,
          name: nodeName,
          showAction: !(isRoot && name === "all"),
          isRoot,
          checkable: disabledCheckAllNode && name === "all" ? false : undefined,
          count: (userMode ? item.user_count : item.devices_count) || 0,
          subCount: userMode
            ? undefined // item.invisible_user_count
            : item.children && item.children.length > 0
            ? item.devices_count_including_children
            : undefined,
          companyId: item.company_id,
          parentId: item.parent_id,
          orderNO: item.order_no || i,
          selectable: (withPermission && item.hasPermission) ?? undefined,
          isLeaf: !item.children || !item.children.length,
          children:
            item.children && item.children.length
              ? formatTree(item.children)
              : []
        };
      });
    };

    const getTreeGroup = async noReset => {
      try {
        if (!noReset) {
          setLoading(true);
        }
        const user_company_id = await localforage.getItem("userCompanyID")
        const data = await StoreApi.GetStoreTreeList(currentCompanyId ? currentCompanyId : user_company_id );
        if (data && data.groups && data.groups.length > 0) {
          const treeData = formatTree(
            data.groups.filter(group => (!showAll ? group.id !== "all" : true))
          );
          setTreeData(treeData);
          if (!noReset) {
            setExpandedKeys(treeData.map(item => item.key));
            if (treeData.length > 1 && !selectedKeysFromPropsRef.current) {
              setSelectedKeys([treeData[0].key]);
            }
            if (defaultSelectAll) {
              setCheckedKeys(treeData.map(item => item.key));
            }
            onLoaded?.(treeData);
          }
        } else {
          setTreeData([]);
        }
      } catch (e) {
        onLoadFailed?.(e);
      } finally {
        if (!noReset) {
          setLoading(false);
        }
      }
    };

    const handleNodeMove = async result => {
      const user_company_id = await localforage.getItem("userCompanyID")
      await moveGroupAPI({
        groups: result.list.map((item, i) => ({
          id: item.id,
          title: item.title,
          parent_id: item.parentId,
          order_no: i
        })),
        company_id: currentCompanyId ? currentCompanyId : user_company_id
      });
    };

    const handleNodeRename = async (node, name) => {
      onChangeRename?.(node, name);
      await updateGroupApi({
        id: node.id,
        name
      });
    };

    const handleNodeAdd = async (node, name) => {
      // 当前公司id作为仓储(default)节点id
      const defaultNode =
        node.parentId === node.companyId || node.parentId === "default";
      const user_company_id = await localforage.getItem("userCompanyID")
      return createGroupApi({
        name,
        parent_id: defaultNode ? "" : node.parentId,
        order_no: node.orderNO,
        company_id: currentCompanyId ? currentCompanyId : user_company_id
      });
    };

    const handleNodeDelete = async node => {
      const user_company_id = await localforage.getItem("userCompanyID")
      await deleteGroupApi({
        id: node.id,
        company_id: currentCompanyId ? currentCompanyId : user_company_id
      });
    };

    useImperativeHandle(ref, () => ({
      reload: getTreeGroup,
      getTreeData: () => treeData,
      getFlattenTreeData: () => {
        return flattenTreeData(treeData);
      },
      setSelectedKeys
    }));

    useEffect(() => {
      getTreeGroup();
    }, [currentCompanyId]);

    useEffect(() => {
      if (checkableFromProps !== undefined) {
        setCheckable(checkableFromProps);
      }
    }, [checkableFromProps]);

    useEffect(() => {
      if (checkedKeysFromProps !== undefined) {
        setCheckedKeys(checkedKeysFromProps);
      }
    }, [checkedKeysFromProps]);

    useEffect(() => {
      if (selectedKeysFromProps !== undefined) {
        setSelectedKeys(selectedKeysFromProps);
      }
    }, [selectedKeysFromProps]);
    // console.log("-------readOnly----gggkkk----",readOnly)
    return (
      <div>
        <CompanySelector
          companyId={currentCompanyId}
          onSelect={handleCompanySelect}
        />
        {showSwitch && (
          <div className={styles.switch}>
            <span>
              {checkable
                ? t({ defaultMessage: "切换单选" })
                : t({ defaultMessage: "切换多选" })}
            </span>

            <IconButton
              onClick={() => {
                setCheckable(prev => !prev);
                setCheckedKeys([]);
                onSwitch?.(!checkable);
              }}
            >
              <IconSwitch />
            </IconButton>
          </div>
        )}
        <SearchInput
          placeholder={t({ defaultMessage: "搜索门店" })}
          iconPosition="left"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
        />
        {description}
        <CategoryContent collapse={collapse}>
          <CategoryTree
            checkStrictly={checkStrictly}
            editable={editable}
            sortable={sortable}
            importable={importable}
            searchValue={searchValue}
            setTreeData={setTreeData}
            treeData={treeData}
            selectable={selectable}
            checkable={checkable}
            showAll={showAll}
            readOnly={readOnly}
            expandedKeys={expandedKeys}
            selectedKeys={selectedKeys}
            checkedKeys={checkedKeys}
            onCheck={onCheck}
            onSelect={onSelect}
            onNodeMove={handleNodeMove}
            onNodeRename={handleNodeRename}
            onInputRename={onInputRename}
            onNodeAdd={handleNodeAdd}
            onNodeDelete={handleNodeDelete}
            onNodeImport={onNodeImport}
          />
        </CategoryContent>
      </div>
    );
  }
);

export default Category;
